import { ShowAllButton } from "components/ShowAllButton/ShowAllButton";
import { ShowDeletedFilter } from "containers/Filter/components/ShowDeletedFilter";
import { ReactNode, useCallback, useRef } from "react";
import { Form as FinalForm, FormSpy } from "react-final-form";

export interface TrashFilterValues {
    /** "Show in trash" — include trashed rows (maps to allow_deleted). */
    includeDeleted: boolean;
    /** "Show non-trashed" — include normal rows (maps to allow_normal). */
    hideDeleted: boolean;
}

interface TrashFilterHeaderActionProps {
    /**
     * Called ONLY when the trash-filter toggles actually change — never on the
     * initial mount, and never for a repeated identical value. The caller maps
     * the two booleans onto its own query variables (typically
     * `{ allow_deleted: includeDeleted, allow_normal: hideDeleted, offset: 0 }`).
     */
    onTrashChange: (values: TrashFilterValues) => void;
    /** Optional "Show all" jump (typically to a pre-filtered advanced search). */
    onShowAll?: () => void;
    /** Native tooltip for the "Show all" button. */
    showAllTitle?: string;
    /** Override the "Show all" button label (defaults to "Show all"). */
    showAllLabel?: string;
    /** Extra header actions rendered after the trash filter / show-all button. */
    children?: ReactNode;
    /**
     * Initial toggle state. Defaults to the app-wide standard: hide trashed
     * rows, show normal rows ({ includeDeleted: false, hideDeleted: true }).
     */
    defaults?: TrashFilterValues;
}

const DEFAULTS: TrashFilterValues = { includeDeleted: false, hideDeleted: true };

/**
 * Reusable list-header action bundling the standard "show in trash" toggle
 * (via {@link ShowDeletedFilter}) and an optional "Show all" advanced-search
 * jump. This is the single shared implementation of the trash-toggle wiring —
 * the `react-final-form` + `FormSpy` plumbing plus the mount/dedup guard that
 * every resource list would otherwise copy-paste. Mount any list's header with
 * this instead of re-implementing the filter form per screen.
 *
 * The caller keeps ownership of its own query state: `onTrashChange` fires only
 * on a genuine toggle change, and the caller maps the emitted booleans onto its
 * GraphQL/query variables.
 */
export const TrashFilterHeaderAction = ({
    onTrashChange,
    onShowAll,
    showAllTitle,
    showAllLabel,
    children,
    defaults = DEFAULTS,
}: TrashFilterHeaderActionProps) => {
    // Tracks the last values we propagated so the FormSpy's initial (mount)
    // fire — which equals the defaults — does not trigger a redundant query
    // update / refetch, and repeated identical values are ignored. Only a real
    // toggle change propagates.
    const lastRef = useRef<TrashFilterValues>(defaults);

    const handleChange = useCallback(
        (values: { include_deleted?: boolean; hide_deleted?: boolean }) => {
            const includeDeleted = !!values.include_deleted;
            const hideDeleted = !!values.hide_deleted;
            if (
                includeDeleted === lastRef.current.includeDeleted &&
                hideDeleted === lastRef.current.hideDeleted
            ) {
                return;
            }
            lastRef.current = { includeDeleted, hideDeleted };
            onTrashChange({ includeDeleted, hideDeleted });
        },
        [onTrashChange]
    );

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FinalForm
                onSubmit={() => undefined}
                initialValues={{
                    include_deleted: defaults.includeDeleted,
                    hide_deleted: defaults.hideDeleted,
                }}
            >
                {({ values }) => (
                    <>
                        <ShowDeletedFilter
                            includeDeleted={values.include_deleted}
                            hideDeleted={values.hide_deleted}
                        />
                        <FormSpy
                            subscription={{ values: true }}
                            onChange={({ values: spyValues }) => handleChange(spyValues)}
                        />
                    </>
                )}
            </FinalForm>
            {onShowAll && (
                <ShowAllButton onClick={onShowAll} title={showAllTitle} label={showAllLabel} />
            )}
            {children}
        </div>
    );
};

export default TrashFilterHeaderAction;
